import React, { forwardRef } from "react";
import moment from "moment";
import ImageTag from "../../reusebale/imageTag";

const InvoiceTemplate = forwardRef(({ invoice, landlordInfo }, ref) => {
  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const subtotal = invoice.amount;

  return (
    <div ref={ref} className="w-[8.27in] h-[11.69in] mx-auto p-8 bg-white">
      <div className="flex justify-between items-start mb-8">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">INVOICE</h1>
          <div className="text-sm text-gray-600">
            <p className="font-semibold">
              Invoice #
              {`INV-${invoice.invoiceNumber.toString().padStart(6, "0")}`}
            </p>
            <p>Date: {moment(invoice.createdAt).format("MM/DD/YYYY")}</p>
            <p>Due Date: {moment(invoice.startDate).format("MM/DD/YYYY")}</p>
          </div>
        </div>

        <div className="text-right">
          <ImageTag
            path="/assets/logo.png"
            classes="w-full h-32"
            altText="logo"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill To:</h3>
          <div className="text-sm text-gray-700">
            <p className="font-semibold">
              {landlordInfo.firstName} {landlordInfo.lastName}
            </p>
            <p>{landlordInfo.email}</p>
            {landlordInfo.phoneNumber && (
              <p>Phone: {landlordInfo.phoneNumber}</p>
            )}
            <p>Customer ID: {landlordInfo._id}</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Bill From:
          </h3>
          <div className="text-sm text-gray-700">
            <p className="font-semibold">FlexManagement Inc.</p>
            <p>1234 Business District</p>
            <p>Suite 567, City, State 12345</p>
            <p>Email: billing@flexmanagement.com</p>
            <p>Phone: (555) 123-4567</p>
          </div>
        </div>
      </div>

      {/* Subscription Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Subscription Details
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p>
                <span className="font-medium">Plan Name:</span>{" "}
                {invoice.packageData?.planName}
              </p>
              <p>
                <span className="font-medium">Subscription Type:</span>{" "}
                {invoice.subscriptionType}
              </p>
              <p>
                <span className="font-medium">Properties Allowed:</span>{" "}
                {invoice.packageData?.allowProperties}
              </p>
            </div>
            <div>
              <p>
                <span className="font-medium">Duration:</span>{" "}
                {invoice.packageData?.numberOfMonth} months
              </p>
              <p>
                <span className="font-medium">Start Date:</span>{" "}
                {moment(invoice.startDate).format("MM/DD/YYYY")}
              </p>
              <p>
                <span className="font-medium">End Date:</span>{" "}
                {moment(invoice.endDate).format("MM/DD/YYYY")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Items Table */}
      <div className="mb-8">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-3 text-left text-sm font-semibold">
                Description
              </th>
              <th className="border border-gray-300 p-3 text-center text-sm font-semibold">
                Duration
              </th>
              <th className="border border-gray-300 p-3 text-center text-sm font-semibold">
                Properties
              </th>
              <th className="border border-gray-300 p-3 text-right text-sm font-semibold">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 p-3 text-sm">
                <div>
                  <p className="font-medium">{invoice.packageData?.planName}</p>
                  <p className="text-gray-600 text-xs">
                    {invoice.subscriptionPlanName.includes("Add-on")
                      ? "Additional Properties Subscription"
                      : "Property Management Subscription"}
                  </p>
                  {invoice.packageData?.discountPercentage > 0 && (
                    <p className="text-green-600 text-xs">
                      Volume Discount Applied:{" "}
                      {invoice.packageData.discountPercentage}%
                    </p>
                  )}
                </div>
              </td>
              <td className="border border-gray-300 p-3 text-center text-sm">
                {invoice.packageData?.numberOfMonth} months
              </td>
              <td className="border border-gray-300 p-3 text-center text-sm">
                {invoice.packageData?.allowProperties}
              </td>
              <td className="border border-gray-300 p-3 text-right text-sm font-medium">
                {formatCurrency(invoice.amount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pricing Breakdown */}
      {invoice.packageData?.discountAmount &&
        invoice.packageData.discountAmount !== "$0" && (
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-800 mb-3">
              Pricing Breakdown
            </h4>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p>
                    <span className="font-medium">Original Price:</span>{" "}
                    {invoice.packageData.originalPrice}
                  </p>
                  <p>
                    <span className="font-medium">
                      Discount ({invoice.packageData.discountPercentage}%):
                    </span>
                    <span className="text-green-600">
                      {" "}
                      -{invoice.packageData.discountAmount}
                    </span>
                  </p>
                </div>
                <div>
                  <p>
                    <span className="font-medium">Monthly Rate:</span>{" "}
                    {formatCurrency(invoice.packageData?.monthlyPrice)}
                  </p>
                  <p>
                    <span className="font-medium">Per Property:</span>{" "}
                    {formatCurrency(invoice.packageData?.pricePerProperty)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="flex justify-end mb-8">
        <div className="w-64">
          <div className="border-t border-gray-300 mt-2 pt-2">
            <div className="flex justify-between py-2 text-lg font-bold">
              <span>Total:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Payment Information
        </h3>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p>
                <span className="font-medium">Payment Status:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs ${
                    invoice.status === "active"
                      ? "bg-green-100 text-green-800"
                      : invoice.status === "invalid"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {invoice.status === "active" ? "Paid" : invoice.status === "invalid" ? "Failed" : "Pending"}
                </span>
              </p>
              {invoice.lastPaymentDate && (
                <p>
                  <span className="font-medium">Payment Date:</span>{" "}
                  {moment(invoice.lastPaymentDate).format("MM/DD/YYYY")}
                </p>
              )}
            </div>
            <div>
              <p>
                <span className="font-medium">Invoice Status:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs ${
                    invoice.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {invoice.status.charAt(0).toUpperCase() +
                    invoice.status.slice(1)}
                </span>
              </p>
              {invoice.slipStreamTransactionId && (
                <p>
                  <span className="font-medium">Transaction ID:</span>{" "}
                  {invoice.slipStreamTransactionId}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      {/* Add a Footer */}
    </div>
  );
});

export default InvoiceTemplate;
